import Modal from 'LuxKit/Modal/Modal'
import { useCallback, useMemo, useRef, useState } from 'react'
import JourneyV2Details from './JourneyV2Details'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TabButton from 'LuxKit/Tabs/TabButton'
import { last } from 'lib/array/arrayUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import flightBundleGlossaryMessages from 'i18n/flightBundleGlossaryMessages'

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
`

const CarrierLogo = styled.img`
  width: ${rem(16)};
  height: ${rem(16)};
  object-fit: contain;
`

const TabsContainer = styled.div`
  position: sticky;
  top: 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  z-index: 1;
  padding-bottom: ${rem(12)};
`

interface Props {
  journey?: App.JourneyV2
  returningJourney?: App.JourneyV2
  leg?: App.Leg
  legIndex?: number
  fareFamily?: App.FlightFareFamily
  inclusions?: App.InclusionFeatures
  addedBaggage?: App.InclusionFeatures
  isStandaloneFlights?: boolean
  reservationInfo?: App.NDCReservationInfo
  pnrId?: string
  region?: string
  isStandalone?: boolean
  isDeparting?: boolean
  withInsurance?: boolean
}

function JourneyV2DetailsModal(props: Props) {
  const {
    journey,
    returningJourney,
    leg,
    legIndex,
    fareFamily,
    inclusions,
    addedBaggage,
    reservationInfo,
    pnrId,
    region,
    isStandalone,
    withInsurance,
  } = props
  const intl = useIntl()
  const [fareRulesURL, setFareRulesURL] = useState<string | undefined>()
  const [fareRulesParams, setFareRulesParams] = useState<any | undefined>()
  const [activeTab, setActiveTab] = useState<'departing' | 'returning'>('departing')

  const modalRef = useRef<HTMLDivElement>(null)

  const onHideFareRules = useCallback(() => {
    setFareRulesURL(undefined)
    setFareRulesParams(undefined)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const handleTabClick = useCallback((tab: 'departing' | 'returning') => {
    setActiveTab(tab)
    setFareRulesURL(undefined)
    setFareRulesParams(undefined)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const fareRulesTitle = intl.formatMessage({
    id: 'j8rBb_9gIKASVrb2Ldwp',
    defaultMessage: 'Fare Rules',
    description: 'Modal title for fare rules view in the flights section',
  })

  const flightDetailsTitle = intl.formatMessage(flightBundleGlossaryMessages.flightDetails)

  const departingTabLabel = useMemo(() => {
    if (!journey) return ''
    const first = journey.flightGroup.flights[0]
    const lastFlight = last(journey.flightGroup.flights)
    return `${first.departureAirport} - ${lastFlight.arrivalAirport}`
  }, [journey])

  const returningTabLabel = useMemo(() => {
    if (!returningJourney) return ''
    const first = returningJourney.flightGroup.flights[0]
    const lastFlight = last(returningJourney.flightGroup.flights)
    return `${first.departureAirport} - ${lastFlight.arrivalAirport}`
  }, [returningJourney])

  const isDepartingTab = activeTab === 'departing'
  const isReturningTab = activeTab === 'returning'
  const activeJourney = isDepartingTab ? journey : returningJourney

  return <Modal
    title={fareRulesURL ? fareRulesTitle : flightDetailsTitle}
    onBack={fareRulesURL ? onHideFareRules : undefined}
    ref={modalRef}
  >
    {returningJourney && (
      <TabsContainer>
        <TabButtonsGroup fit="grow">
          <TabButton size="small" isActive={isDepartingTab} onClick={() => handleTabClick('departing')}>
            <TabLabel>
              {!!journey?.carrierBrandMark && <CarrierLogo src={journey.carrierBrandMark} alt="" />}
              {departingTabLabel}
            </TabLabel>
          </TabButton>
          <TabButton size="small" isActive={isReturningTab} onClick={() => handleTabClick('returning')}>
            <TabLabel>
              {!!returningJourney?.carrierBrandMark && <CarrierLogo src={returningJourney.carrierBrandMark} alt="" />}
              {returningTabLabel}
            </TabLabel>
          </TabButton>
        </TabButtonsGroup>
      </TabsContainer>
    )}
    <JourneyV2Details
      key={activeTab}
      setFareRulesURL={setFareRulesURL}
      setFareRulesParams={setFareRulesParams}
      fareRulesURL={fareRulesURL}
      fareRulesParams={fareRulesParams}
      journey={activeJourney}
      leg={returningJourney ? undefined : leg}
      legIndex={returningJourney ? undefined : legIndex}
      fareFamily={isDepartingTab ? fareFamily : undefined}
      inclusions={isDepartingTab ? inclusions : undefined}
      addedBaggage={isDepartingTab ? addedBaggage : undefined}
      reservationInfo={isDepartingTab ? reservationInfo : undefined}
      pnrId={isDepartingTab ? pnrId : undefined}
      region={isDepartingTab ? region : undefined}
      isStandalone={isStandalone}
      withInsurance={isDepartingTab ? withInsurance : undefined}
    />
  </Modal>
}

export default JourneyV2DetailsModal
